import { useState, useEffect } from 'react';
import { Phone, MessageCircle, Mail, AlertTriangle, Clock, MoreVertical, Send, Bell, ArrowUpDown, ArrowUp, ArrowDown, Filter, Edit3, PhoneOff, UserX } from 'lucide-react';
import { agentsService, Agent } from '../../services/agents.service';
import { useAgentStatusSync } from '../../hooks/useAgentStatusSync';
import { useAuthStore } from '../../stores/auth.store';
import { useNavigate } from 'react-router-dom';

interface AgentStatus {
  agentId: string;
  employeeId: string;
  email: string;
  firstName: string;
  lastName: string;
  status: string;
  timestamp: string;
  source: 'ZENDESK' | 'KUSTOMER';
}

interface AgentWithAdherence extends Agent {
  tierGroup: TierGroup;
  isAdherent: boolean;
  scheduledActivity: ActivityType | null;
}

type AgentStateType =
  | 'AVAILABLE'
  | 'ON_CALL'
  | 'ON_BREAK'
  | 'LOGGED_OUT'
  | 'MEETING'
  | 'LUNCH'
  | 'OFFLINE'
  | 'CALLOUT'
  | 'NOT_CLOCKED_IN';

type ActivityType =
  | 'PHONE'
  | 'CHAT'
  | 'EMAIL'
  | 'BREAK'
  | 'MEAL'
  | 'MEETING'
  | 'TRAINING'
  | 'COACHING'
  | 'UNSCHEDULED'
  | 'PTO'
  | 'SICK';

type StatusFilter = 'all' | 'AVAILABLE' | 'ON_CALL' | 'ON_BREAK' | 'MEETING' | 'OUT_OF_ADHERENCE' | 'CALLOUT' | 'NOT_CLOCKED_IN';

type TierGroup = 'Tier 1' | 'Tier 2' | 'Supervisor' | 'Spanish' | 'Exec Escalation';

type SortField = 'agent' | 'state' | 'duration' | 'scheduled' | 'source' | 'status';
type SortDirection = 'asc' | 'desc' | null;

const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const getDurationColor = (seconds: number, state: string): string => {
  // Don't color-code breaks and lunch
  if (state === 'ON_BREAK' || state === 'BREAK' || state === 'LUNCH' || state === 'MEAL') {
    return 'text-gray-600';
  }

  // Callouts - show in muted red (they've already called out, just tracking time)
  if (state === 'CALLOUT') {
    return 'text-red-400';
  }

  // Not clocked in - escalating urgency based on how late they are
  if (state === 'NOT_CLOCKED_IN') {
    const minutes = seconds / 60;
    if (minutes < 10) return 'text-amber-500 font-medium';
    if (minutes < 20) return 'text-amber-600 font-semibold';
    return 'text-red-600 font-bold';
  }

  const minutes = seconds / 60;
  if (minutes < 5) return 'text-gray-600';
  if (minutes < 15) return 'text-yellow-600 font-semibold';
  return 'text-red-600 font-bold';
};

const getStateIcon = (state: string, channel?: string) => {
  if (state === 'ON_CALL') {
    if (channel === 'PHONE') return <Phone className="h-4 w-4" />;
    if (channel === 'CHAT') return <MessageCircle className="h-4 w-4" />;
    if (channel === 'EMAIL') return <Mail className="h-4 w-4" />;
  }
  if (state === 'CALLOUT') return <PhoneOff className="h-4 w-4" />;
  if (state === 'NOT_CLOCKED_IN') return <UserX className="h-4 w-4" />;
  return null;
};

const getStateColor = (state: string, isAdherent: boolean = true) => {
  if (!isAdherent && state === 'LOGGED_OUT') {
    return {
      bg: 'bg-red-100',
      text: 'text-red-800',
      border: 'border-red-300',
      card: 'bg-red-50',
      rowBorder: 'border-l-4 border-l-red-500'
    };
  }

  switch (state) {
    case 'AVAILABLE':
      return { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200', card: 'bg-green-50', rowBorder: '' };
    case 'ON_CALL':
      return { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200', card: 'bg-blue-50', rowBorder: '' };
    case 'ON_BREAK':
    case 'BREAK':
      return { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200', card: 'bg-orange-50', rowBorder: '' };
    case 'LUNCH':
    case 'MEAL':
      return { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200', card: 'bg-orange-50', rowBorder: '' };
    case 'MEETING':
    case 'TRAINING':
    case 'COACHING':
      return { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200', card: 'bg-purple-50', rowBorder: '' };
    case 'LOGGED_OUT':
    case 'OFFLINE':
      return { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200', card: 'bg-gray-50', rowBorder: '' };
    case 'CALLOUT':
      return { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200', card: 'bg-red-50', rowBorder: 'border-l-4 border-l-red-400' };
    case 'NOT_CLOCKED_IN':
      return { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-200', card: 'bg-amber-50', rowBorder: 'border-l-4 border-l-amber-400' };
    default:
      return { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200', card: 'bg-gray-50', rowBorder: '' };
  }
};

const formatStateName = (state: string, channel?: string): string => {
  // Special formatting for specific states
  if (state === 'NOT_CLOCKED_IN') return 'Not Clocked In';
  if (state === 'CALLOUT') return 'Called Out';

  const formatted = state
    .split('_')
    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');

  if ((state === 'ON_CALL' || state === 'AFTER_CALL_WORK') && channel) {
    return `${formatted} - ${channel.charAt(0) + channel.slice(1).toLowerCase()}`;
  }

  return formatted;
};

// Check if agent is adhering to schedule
const checkAdherence = (
  currentState: AgentStateType,
  scheduledActivity: ActivityType | null
): boolean => {
  if (!scheduledActivity) return true;

  // Callouts and not-clocked-in are special states - they're not adherent if they should be working
  if (currentState === 'CALLOUT') {
    return scheduledActivity === 'SICK' || scheduledActivity === 'PTO';
  }
  if (currentState === 'NOT_CLOCKED_IN') {
    return false; // Always out of adherence if scheduled to work
  }

  // Map state to expected activities
  const stateToActivity: Record<AgentStateType, ActivityType[]> = {
    'ON_CALL': ['PHONE', 'CHAT', 'EMAIL'],
    'AVAILABLE': ['PHONE', 'CHAT', 'EMAIL'],
    'ON_BREAK': ['BREAK'],
    'LUNCH': ['MEAL'],
    'MEETING': ['MEETING', 'TRAINING', 'COACHING'],
    'OFFLINE': ['UNSCHEDULED', 'PTO', 'SICK'],
    'LOGGED_OUT': ['UNSCHEDULED', 'PTO', 'SICK'],
    'CALLOUT': ['SICK', 'PTO'],
    'NOT_CLOCKED_IN': [],
  };

  const expectedActivities = stateToActivity[currentState] || [];
  return expectedActivities.includes(scheduledActivity);
};

// Simulate realistic agent state changes for demo (only active states - callouts/not clocked in are assigned at init)
const getRandomState = (): AgentStateType => {
  const states: AgentStateType[] = ['AVAILABLE', 'ON_CALL', 'ON_BREAK', 'MEETING', 'LUNCH'];
  const weights = [30, 45, 10, 10, 5]; // Higher weight for AVAILABLE and ON_CALL
  const totalWeight = weights.reduce((a, b) => a + b, 0);
  const random = Math.random() * totalWeight;

  let sum = 0;
  for (let i = 0; i < states.length; i++) {
    sum += weights[i];
    if (random < sum) return states[i];
  }
  return 'AVAILABLE';
};

// Assign initial state including callouts and not-clocked-in (used only at initialization)
const getInitialState = (index: number): AgentStateType => {
  // Use index-based seeding for consistent demo data
  const seed = (index * 17) % 100;

  // ~5% callouts, ~8% not clocked in, rest are active
  if (seed < 5) return 'CALLOUT';
  if (seed < 13) return 'NOT_CLOCKED_IN';
  return getRandomState();
};

// Assign tier groups to agents (for demo)
const assignTierGroup = (index: number): TierGroup => {
  const tierGroups: TierGroup[] = ['Tier 1', 'Tier 2', 'Supervisor', 'Spanish', 'Exec Escalation'];
  const weights = [50, 30, 10, 7, 3]; // Tier 1 most common
  const totalWeight = weights.reduce((a, b) => a + b, 0);

  // Use index as seed for consistent assignment
  const seed = (index * 73) % totalWeight;
  let sum = 0;
  for (let i = 0; i < tierGroups.length; i++) {
    sum += weights[i];
    if (seed < sum) return tierGroups[i];
  }
  return 'Tier 1';
};

export default function RealtimePage() {
  const [agents, setAgents] = useState<AgentWithAdherence[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [filterAdherence, setFilterAdherence] = useState<'all' | 'adherent' | 'non-adherent'>('all');
  const [openActionsMenu, setOpenActionsMenu] = useState<string | null>(null);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [notificationMessage, setNotificationMessage] = useState('');

  // Sorting state
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  // Filter dropdowns state
  const [openFilter, setOpenFilter] = useState<SortField | null>(null);
  const [columnFilters, setColumnFilters] = useState<{
    state: string[];
    scheduled: string[];
    source: string[];
    status: string[];
  }>({
    state: [],
    scheduled: [],
    source: [],
    status: [],
  });

  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();

  // Fetch initial agent data
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const data = await agentsService.getAllWithCurrentState();
        // Initialize agents with states for demo
        const agentsWithStates: AgentWithAdherence[] = data.map((agent, index) => {
          const initialState = getInitialState(index);

          // Calculate appropriate duration and scheduled activity based on state
          let duration = Math.floor(Math.random() * 300); // Random duration 0-5 minutes for active states
          let scheduledActivity: ActivityType = 'PHONE'; // Default scheduled activity

          if (initialState === 'CALLOUT') {
            // Callouts typically happen before shift - show how long since they called out
            duration = Math.floor(Math.random() * 7200) + 1800; // 30 min to 2.5 hours
          } else if (initialState === 'NOT_CLOCKED_IN') {
            // Not clocked in - show how late they are
            duration = Math.floor(Math.random() * 1800) + 300; // 5 to 35 minutes late
          }

          return {
            ...agent,
            tierGroup: assignTierGroup(index),
            isAdherent: true,
            scheduledActivity,
            currentState: agent.currentState || {
              state: initialState,
              startTime: new Date(Date.now() - duration * 1000).toISOString(),
              duration,
              source: 'ZENDESK_WEBHOOK' as const,
            }
          };
        });
        setAgents(agentsWithStates);
      } catch (error) {
        console.error('Failed to fetch agents:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAgents();
  }, []);

  // Simulate live demo data updates every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setAgents(prevAgents => {
        return prevAgents.map(agent => {
          const currentState = agent.currentState?.state;

          // Don't change callouts or not-clocked-in agents (they remain until manually updated)
          if (currentState === 'CALLOUT' || currentState === 'NOT_CLOCKED_IN') {
            return {
              ...agent,
              currentState: agent.currentState ? {
                ...agent.currentState,
                duration: (agent.currentState.duration || 0) + 3,
              } : null
            };
          }

          // Randomly update some agents (30% chance per update)
          if (Math.random() < 0.3) {
            const shouldChangeState = Math.random() < 0.15; // 15% chance to change state

            if (shouldChangeState) {
              return {
                ...agent,
                currentState: {
                  state: getRandomState(),
                  startTime: new Date().toISOString(),
                  duration: 0,
                  source: agent.currentState?.source || 'ZENDESK_WEBHOOK',
                }
              };
            } else {
              // Just increment duration
              return {
                ...agent,
                currentState: agent.currentState ? {
                  ...agent.currentState,
                  duration: (agent.currentState.duration || 0) + 3,
                } : null
              };
            }
          }

          // Increment duration for agents not changing state
          return {
            ...agent,
            currentState: agent.currentState ? {
              ...agent.currentState,
              duration: (agent.currentState.duration || 0) + 3,
            } : null
          };
        });
      });
    }, 3000); // Update every 3 seconds

    return () => clearInterval(interval);
  }, []);

  // Subscribe to real-time agent status updates
  useAgentStatusSync({
    organizationId: user?.organizationId || '',
    onStatusChange: (agentStatus: AgentStatus) => {
      setAgents((prevAgents) =>
        prevAgents.map((agent) =>
          agent.id === agentStatus.agentId
            ? {
                ...agent,
                currentState: {
                  state: agentStatus.status as any,
                  startTime: agentStatus.timestamp,
                  duration: 0,
                  source: agentStatus.source === 'ZENDESK' ? 'ZENDESK_WEBHOOK' : 'KUSTOMER_WEBHOOK',
                },
              }
            : agent
        )
      );
    },
  });

  // Calculate agent adherence (mock for demo - would come from backend)
  const agentsWithAdherence: AgentWithAdherence[] = agents.map(agent => {
    const state = agent.currentState?.state as AgentStateType || 'OFFLINE';
    // Mock scheduled activity - in real app this would come from schedule data
    const scheduledActivity: ActivityType | null = 'PHONE'; // Assume working hours
    const isAdherent = checkAdherence(state, scheduledActivity);

    return {
      ...agent,
      isAdherent,
      scheduledActivity,
    };
  });

  // Calculate status metrics
  const statusMetrics = [
    {
      label: 'Available',
      count: agentsWithAdherence.filter((a) => a.currentState?.state === 'AVAILABLE').length,
      color: 'green',
      filterKey: 'AVAILABLE' as StatusFilter,
    },
    {
      label: 'On Call',
      count: agentsWithAdherence.filter((a) => a.currentState?.state === 'ON_CALL').length,
      color: 'blue',
      filterKey: 'ON_CALL' as StatusFilter,
    },
    {
      label: 'On Break',
      count: agentsWithAdherence.filter((a) =>
        a.currentState?.state === 'ON_BREAK' || a.currentState?.state === 'LUNCH'
      ).length,
      color: 'orange',
      filterKey: 'ON_BREAK' as StatusFilter,
    },
    {
      label: 'In Meeting',
      count: agentsWithAdherence.filter((a) => a.currentState?.state === 'MEETING').length,
      color: 'purple',
      filterKey: 'MEETING' as StatusFilter,
    },
    {
      label: 'Not Clocked In',
      count: agentsWithAdherence.filter((a) => a.currentState?.state === 'NOT_CLOCKED_IN').length,
      color: 'amber',
      filterKey: 'NOT_CLOCKED_IN' as StatusFilter,
    },
    {
      label: 'Callouts',
      count: agentsWithAdherence.filter((a) => a.currentState?.state === 'CALLOUT').length,
      color: 'red',
      filterKey: 'CALLOUT' as StatusFilter,
    },
    {
      label: 'Out of Adherence',
      count: agentsWithAdherence.filter((a) => !a.isAdherent).length,
      color: 'rose',
      filterKey: 'OUT_OF_ADHERENCE' as StatusFilter,
    },
  ];

  // Handle column sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get unique values for filter dropdowns
  const getUniqueStates = () => {
    const states = new Set(agentsWithAdherence.map(a => a.currentState?.state || 'OFFLINE'));
    return Array.from(states).sort();
  };

  const getUniqueScheduled = () => {
    const scheduled = new Set(agentsWithAdherence.map(a => a.scheduledActivity || 'PHONE'));
    return Array.from(scheduled).sort();
  };

  const getUniqueSources = () => {
    const sources = new Set(agentsWithAdherence.map(a => a.currentState?.source || 'ZENDESK'));
    return Array.from(sources).sort();
  };

  const getUniqueStatuses = () => ['Adherent', 'Out of Adherence'];

  // Toggle column filter
  const toggleColumnFilter = (field: SortField, value: string) => {
    setColumnFilters(prev => {
      const currentFilters = prev[field as keyof typeof prev] || [];
      const isSelected = currentFilters.includes(value);

      return {
        ...prev,
        [field]: isSelected
          ? currentFilters.filter(v => v !== value)
          : [...currentFilters, value],
      };
    });
  };

  // Filter agents by status, adherence, and column filters
  const filteredAgents = agentsWithAdherence.filter((agent) => {
    const fullName = `${agent.firstName} ${agent.lastName}`.toLowerCase();
    const matchesSearch = fullName.includes(searchQuery.toLowerCase()) ||
                         agent.email.toLowerCase().includes(searchQuery.toLowerCase());

    // Status filter (from metric cards)
    let matchesStatus = true;
    if (statusFilter === 'AVAILABLE') matchesStatus = agent.currentState?.state === 'AVAILABLE';
    else if (statusFilter === 'ON_CALL') matchesStatus = agent.currentState?.state === 'ON_CALL';
    else if (statusFilter === 'ON_BREAK') matchesStatus = agent.currentState?.state === 'ON_BREAK' || agent.currentState?.state === 'LUNCH';
    else if (statusFilter === 'MEETING') matchesStatus = agent.currentState?.state === 'MEETING';
    else if (statusFilter === 'CALLOUT') matchesStatus = agent.currentState?.state === 'CALLOUT';
    else if (statusFilter === 'NOT_CLOCKED_IN') matchesStatus = agent.currentState?.state === 'NOT_CLOCKED_IN';
    else if (statusFilter === 'OUT_OF_ADHERENCE') matchesStatus = !agent.isAdherent;

    // Column filters
    const matchesStateFilter = columnFilters.state.length === 0 ||
      columnFilters.state.includes(agent.currentState?.state || 'OFFLINE');

    const matchesScheduledFilter = columnFilters.scheduled.length === 0 ||
      columnFilters.scheduled.includes(agent.scheduledActivity || 'PHONE');

    const matchesSourceFilter = columnFilters.source.length === 0 ||
      columnFilters.source.includes(agent.currentState?.source || 'ZENDESK');

    const matchesStatusColFilter = columnFilters.status.length === 0 ||
      (columnFilters.status.includes('Adherent') && agent.isAdherent) ||
      (columnFilters.status.includes('Out of Adherence') && !agent.isAdherent);

    // Adherence filter
    if (filterAdherence === 'adherent') {
      return matchesSearch && matchesStatus && agent.isAdherent &&
        matchesStateFilter && matchesScheduledFilter && matchesSourceFilter && matchesStatusColFilter;
    }
    if (filterAdherence === 'non-adherent') {
      return matchesSearch && matchesStatus && !agent.isAdherent &&
        matchesStateFilter && matchesScheduledFilter && matchesSourceFilter && matchesStatusColFilter;
    }
    return matchesSearch && matchesStatus &&
      matchesStateFilter && matchesScheduledFilter && matchesSourceFilter && matchesStatusColFilter;
  });

  // Sort filtered agents
  const sortedAgents = [...filteredAgents].sort((a, b) => {
    if (!sortField || !sortDirection) return 0;

    const direction = sortDirection === 'asc' ? 1 : -1;

    switch (sortField) {
      case 'agent':
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return nameA.localeCompare(nameB) * direction;

      case 'state':
        const stateA = a.currentState?.state || 'OFFLINE';
        const stateB = b.currentState?.state || 'OFFLINE';
        return stateA.localeCompare(stateB) * direction;

      case 'duration':
        const durationA = a.currentState?.duration || 0;
        const durationB = b.currentState?.duration || 0;
        return (durationA - durationB) * direction;

      case 'scheduled':
        const schedA = a.scheduledActivity || '';
        const schedB = b.scheduledActivity || '';
        return schedA.localeCompare(schedB) * direction;

      case 'source':
        const sourceA = a.currentState?.source || '';
        const sourceB = b.currentState?.source || '';
        return sourceA.localeCompare(sourceB) * direction;

      case 'status':
        const statusA = a.isAdherent ? 0 : 1;
        const statusB = b.isAdherent ? 0 : 1;
        return (statusA - statusB) * direction;

      default:
        return 0;
    }
  });

  const handleSendNotification = () => {
    if (!selectedAgent || !notificationMessage.trim()) return;

    // In a real app, this would call an API to send the notification
    console.log('Sending notification to:', selectedAgent.email);
    console.log('Message:', notificationMessage);

    // Show success message (you could add a toast here)
    alert(`Notification sent to ${selectedAgent.firstName} ${selectedAgent.lastName}`);

    // Reset
    setShowNotificationModal(false);
    setSelectedAgent(null);
    setNotificationMessage('');
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3 text-gray-400" />;
    }
    if (sortDirection === 'asc') {
      return <ArrowUp className="h-3 w-3 text-blue-600" />;
    }
    return <ArrowDown className="h-3 w-3 text-blue-600" />;
  };

  const FilterDropdown = ({ field, values, label }: { field: keyof typeof columnFilters; values: string[]; label: string }) => {
    const isOpen = openFilter === field;
    const activeFilters = columnFilters[field];

    return (
      <div className="relative inline-block">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setOpenFilter(isOpen ? null : field as SortField);
          }}
          className={`p-1 rounded hover:bg-gray-200 ${activeFilters.length > 0 ? 'text-blue-600' : 'text-gray-400'}`}
        >
          <Filter className="h-3 w-3" />
        </button>

        {isOpen && (
          <div className="absolute left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20" onClick={(e) => e.stopPropagation()}>
            <div className="p-2 max-h-60 overflow-y-auto">
              <div className="text-xs font-semibold text-gray-700 px-2 py-1">{label}</div>
              {values.map(value => (
                <label key={value} className="flex items-center px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={activeFilters.includes(value)}
                    onChange={() => toggleColumnFilter(field as SortField, value)}
                    className="mr-2 h-3 w-3 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{value}</span>
                </label>
              ))}
            </div>
            <div className="border-t border-gray-200 p-2 flex justify-between">
              <button
                onClick={() => setColumnFilters(prev => ({ ...prev, [field]: [] }))}
                className="text-xs text-gray-600 hover:text-gray-900"
              >
                Clear
              </button>
              <button
                onClick={() => setOpenFilter(null)}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-500">Loading agents...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Real-Time Agent Status</h1>
          <p className="text-gray-600">Monitor live agent status and adherence</p>
        </div>

        {/* Status Metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {statusMetrics.map((metric) => (
            <div
              key={metric.label}
              onClick={() => {
                setStatusFilter(statusFilter === metric.filterKey ? 'all' : metric.filterKey);
                setFilterAdherence('all');
              }}
              className={`bg-white p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${
                statusFilter === metric.filterKey ? 'ring-2 ring-offset-2 ring-blue-500' : 'border-gray-200'
              }`}
            >
              <div className="text-sm font-medium text-gray-600 mb-1">{metric.label}</div>
              <div className={`text-2xl font-bold text-${metric.color}-600`}>
                {metric.count}
              </div>
            </div>
          ))}
        </div>

        {/* Search and Filters */}
        <div className="bg-white border-2 border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search agents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setFilterAdherence('all')}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  filterAdherence === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Agents
              </button>
              <button
                onClick={() => setFilterAdherence('adherent')}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  filterAdherence === 'adherent'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Adherent
              </button>
              <button
                onClick={() => setFilterAdherence('non-adherent')}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  filterAdherence === 'non-adherent'
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Out of Adherence
              </button>
            </div>
          </div>
        </div>

        {/* Agents Table */}
        <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th
                    onClick={() => handleSort('agent')}
                    className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-2">
                      Agent
                      <SortIcon field="agent" />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('state')}
                    className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-2">
                      Current State
                      <SortIcon field="state" />
                      <FilterDropdown field="state" values={getUniqueStates()} label="Filter State" />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('duration')}
                    className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-2">
                      Duration
                      <SortIcon field="duration" />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('scheduled')}
                    className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-2">
                      Scheduled
                      <SortIcon field="scheduled" />
                      <FilterDropdown field="scheduled" values={getUniqueScheduled()} label="Filter Scheduled" />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('source')}
                    className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-2">
                      Source
                      <SortIcon field="source" />
                      <FilterDropdown field="source" values={getUniqueSources()} label="Filter Source" />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('status')}
                    className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-2">
                      Status
                      <SortIcon field="status" />
                      <FilterDropdown field="status" values={getUniqueStatuses()} label="Filter Status" />
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedAgents.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      {searchQuery ? 'No agents found matching your search' : 'No agents available'}
                    </td>
                  </tr>
                ) : (
                  sortedAgents.map((agent) => {
                    const state = agent.currentState?.state || 'OFFLINE';
                    const isAdherent = agent.isAdherent;
                    const colors = getStateColor(state, isAdherent);
                    const duration = agent.currentState?.duration || 0;
                    const durationColor = getDurationColor(duration, state);

                    return (
                      <tr
                        key={agent.id}
                        className={`hover:bg-gray-50 ${colors.rowBorder}`}
                      >
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          <div className="flex flex-col">
                            <div className="flex items-center">
                              {!isAdherent && (
                                <AlertTriangle className="h-4 w-4 text-red-600 mr-2" />
                              )}
                              {agent.firstName} {agent.lastName}
                            </div>
                            <span className="text-xs text-gray-500 mt-1">{agent.tierGroup}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}>
                            {getStateIcon(state, 'PHONE')}
                            <span className={getStateIcon(state, 'PHONE') ? 'ml-1' : ''}>
                              {formatStateName(state, 'PHONE')}
                            </span>
                          </span>
                        </td>
                        <td className={`px-4 py-3 text-sm ${durationColor}`}>
                          <div className="flex flex-col">
                            <div className="flex items-center">
                              <Clock className="h-3 w-3 mr-1" />
                              {agent.currentState ? formatDuration(duration) : '-'}
                            </div>
                            {state === 'NOT_CLOCKED_IN' && (
                              <span className="text-xs text-amber-600 mt-0.5">late</span>
                            )}
                            {state === 'CALLOUT' && (
                              <span className="text-xs text-red-400 mt-0.5">since callout</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                            {agent.scheduledActivity || 'PHONE'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {agent.currentState?.source ? (
                            <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                              {agent.currentState.source}
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {isAdherent ? (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Adherent
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"
                              title={`Scheduled for ${agent.scheduledActivity}, currently ${state}`}
                            >
                              Out of Adherence
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="relative">
                            <button
                              onClick={() => setOpenActionsMenu(openActionsMenu === agent.id ? null : agent.id)}
                              className="p-1 hover:bg-gray-100 rounded transition-colors"
                            >
                              <MoreVertical className="h-4 w-4 text-gray-600" />
                            </button>

                            {openActionsMenu === agent.id && (
                              <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                                <button
                                  onClick={() => {
                                    setSelectedAgent(agent);
                                    setNotificationMessage(`Hi ${agent.firstName}, please check your schedule for today.`);
                                    setShowNotificationModal(true);
                                    setOpenActionsMenu(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                                >
                                  <Send className="h-4 w-4" />
                                  Send Notification
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedAgent(agent);
                                    setNotificationMessage(`URGENT: You are currently out of adherence. Please return to your scheduled activity.`);
                                    setShowNotificationModal(true);
                                    setOpenActionsMenu(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left border-t border-gray-100"
                                >
                                  <Bell className="h-4 w-4" />
                                  Send Adherence Alert
                                </button>
                                <button
                                  onClick={() => {
                                    navigate('/schedules/canvas', {
                                      state: {
                                        agentId: agent.id,
                                        date: new Date().toISOString().split('T')[0]
                                      }
                                    });
                                    setOpenActionsMenu(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left border-t border-gray-100"
                                >
                                  <Edit3 className="h-4 w-4" />
                                  Update Schedule
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Notification Modal */}
      {showNotificationModal && selectedAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowNotificationModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Send Notification to {selectedAgent.firstName} {selectedAgent.lastName}
            </h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message
              </label>
              <textarea
                value={notificationMessage}
                onChange={(e) => setNotificationMessage(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your message here..."
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowNotificationModal(false);
                  setSelectedAgent(null);
                  setNotificationMessage('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendNotification}
                disabled={!notificationMessage.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Send Notification
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
